import { useEffect, useMemo, useState } from 'react';

export interface ReportColumn {
  id: string;
  label: string;
}

interface ReportColumnsModalProps {
  open: boolean;
  /** All columns the selected report produces. */
  columns: ReportColumn[];
  loading?: boolean;
  onClose: () => void;
  /** Called with the ordered, selected columns to insert as a table. */
  onInsert: (columns: ReportColumn[]) => void;
}

/** Escapes a string for safe inclusion in generated HTML text. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Builds a Handlebars report table for the given columns. Rows loop over the
 * report `data` array; each cell looks up the column key on the row.
 */
export function buildReportColumnsTable(columns: ReportColumn[]): string {
  const headerCells = columns
    .map(
      (c) =>
        `        <th style="text-align:left;border-bottom:2px solid #dee2e6;padding:6px 8px;font-size:11px;">${escapeHtml(
          c.label,
        )}</th>`,
    )
    .join('\n');
  const bodyCells = columns
    .map(
      (c) =>
        `        <td style="border-bottom:1px solid #eee;padding:6px 8px;font-size:11px;">{{lookup this "${c.id}"}}</td>`,
    )
    .join('\n');

  return `<table style="width:100%;border-collapse:collapse;margin:12px 0;">
  <thead>
    <tr>
${headerCells}
    </tr>
  </thead>
  <tbody>
    {{#each data}}
    <tr>
${bodyCells}
    </tr>
    {{/each}}
  </tbody>
</table>`;
}

/**
 * Modal for picking and ordering the columns of the selected report to insert
 * as a table block into the template.
 */
export function ReportColumnsModal({
  open,
  columns,
  loading = false,
  onClose,
  onInsert,
}: ReportColumnsModalProps) {
  const [selected, setSelected] = useState<ReportColumn[]>([]);

  // Default to all columns selected whenever the modal opens or columns change.
  useEffect(() => {
    if (open) setSelected(columns);
  }, [open, columns]);

  const order = useMemo(() => new Map(columns.map((c, i) => [c.id, i])), [columns]);
  const allSelected = columns.length > 0 && selected.length === columns.length;

  if (!open) return null;

  const toggle = (col: ReportColumn) => {
    setSelected((prev) => {
      if (prev.some((c) => c.id === col.id)) return prev.filter((c) => c.id !== col.id);
      return [...prev, col].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    });
  };

  const move = (index: number, dir: -1 | 1) => {
    setSelected((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Insert report columns"
    >
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Insert Report Columns
            </h3>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Pick the columns to print and set their order. A table using these columns is inserted
              into the template.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              Loading report columns…
            </div>
          ) : columns.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No columns found for this report.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Available */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Available ({columns.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(allSelected ? [] : columns)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                  {columns.map((col) => {
                    const checked = selected.some((c) => c.id === col.id);
                    return (
                      <label
                        key={col.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(col)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Selected + order */}
              <div>
                <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Selected ({selected.length})
                </span>
                {selected.length === 0 ? (
                  <p className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                    Select at least one column to insert.
                  </p>
                ) : (
                  <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                    {selected.map((col, index) => (
                      <li
                        key={col.id}
                        className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-300">
                          {index + 1}. {col.label}
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => move(index, -1)}
                            disabled={index === 0}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                            aria-label={`Move ${col.label} up`}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 15.75 7.5-7.5 7.5 7.5"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => move(index, 1)}
                            disabled={index === selected.length - 1}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                            aria-label={`Move ${col.label} down`}
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m19.5 8.25-7.5 7.5-7.5-7.5"
                              />
                            </svg>
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onInsert(selected)}
            disabled={selected.length === 0}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Insert table
          </button>
        </div>
      </div>
    </div>
  );
}
